//------------------------------------------------------------------------------
// BFA10103G 機器設備一覧
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { CellType } from '@grapecity/wijmo.grid';
import { GridItems } from '@/utils/grid-items';
import CustomFlexGrid from '@/components/molecules/CustomFlexGrid';

// 属性定数
const READONLY = { isReadOnly: true };  // 編集不可
const REQUIRED = { isRequired: true };  // 必須

// 一覧項目
const GRID_COLUMNS = new GridItems()
  .text('bukkenNo',        72, '物件\n番号', READONLY)
  .text('areaName',        80, '地区',       READONLY)
  .text('lineName',       100, '冠称',       READONLY)
  .text('poleDisp',       120, '電柱番号',   READONLY)
  .text('poleKind',        80, '施設名',     READONLY)
  .list('idoType',        100, '異動種別',   READONLY)
  .list('setubiCode',     180, '架設設備',   REQUIRED)
  .group('添付写真')
    .cell('picture',       72, '申込時', { text: '添付'})
    .text('hasPic',        72, '有無', READONLY)
    .end()
  .numb('tijyoHeight',     72, '地上高\n(m)', { format: 'n2' })
  .list('nearSetubi',     200, '最接近配電設備', REQUIRED)
  .numb('rikakuHaiden',   100, '配電設備\nとの離隔(m)', {
    format: 'n1',
    ...REQUIRED,
   })
  .group('通信線との離隔(m)')
    .numb('rikakuUpper',   72, '上位', { format: 'n1' })
    .numb('rikakuLower',   72, '下位', { format: 'n1' })
    .end()
;

// グリッド属性
const GRID_PROPS = {
  headersVisibility: 'All',      // 行ヘッダー表示
  rowHeaderType: 'edit',         // 編集ボタン有効
  DeleteOn: true,                // 削除ボタン有効
  filterOn: true,                // フィルタ有効
  allowSorting:  'None',         // ソート不可
  frozenColumns: 5,              // 列固定
  style: { maxHeight: '50vh' },  // 最大高

  // フィルタ無効列
  exceptFilters: [ 'bukkenNo', 'picture' ],
};

/**
 * 機器設備一覧
 */
export default function SetubiListGrid({
  gridRef,
  devCodeMap,
  nearDevMap,
  idoTypeMap,
  isReadOnly,
  onItemEdit,
  onItemSelect,
  onItemUpdate,
}) {

  // 初期化処理
  const handleInitialize = React.useCallback((flexGrid) => {
    flexGrid.hostElement.addEventListener('click', (e) => {
      const hit = flexGrid.hitTest(e);

      // 設備選択 (行頭セルクリック)
      if (hit.cellType === CellType.RowHeader) {
        const item = flexGrid.rows[hit.row].dataItem;
        onItemSelect(item);
      }
    });
  }, [onItemSelect]);

  // 一覧更新処理
  const handleSourceChange = React.useCallback((s, e) => {
    s.collectionView?.collectionChanged?.addHandler((cv, ev) => {
      onItemUpdate(cv.sourceCollection);
    });
  }, [onItemUpdate]);

  // 編集ボタン処理
  const handleItemEdit = React.useCallback((s, setubi, e) => {
    onItemEdit(setubi);
  }, [onItemEdit]);

  // 一覧項目設定
  const gridColumns = React.useMemo(() => GRID_COLUMNS.createComponents({

    // プルダウン設定
    setubiCode: { dataMap: devCodeMap },    // 架設設備コード
    nearSetubi: { dataMap: nearDevMap },    // 最接近配電設備
    idoType:    { dataMap: idoTypeMap },    // 移動種別
  }), [devCodeMap, nearDevMap, idoTypeMap]);

  //
  // 描画
  //
  return (
    <CustomFlexGrid
      ref={gridRef}
      {...GRID_PROPS}
      isReadOnly={isReadOnly}
      editPopupItems={handleItemEdit}
      initializedFunction={handleInitialize}
      itemsSourceChangedFunction={handleSourceChange}
    >
      {gridColumns}
    </CustomFlexGrid>
  );
}

// プロパティ
SetubiListGrid.propTypes = {
  gridRef:     PropTypes.object.isRequired,    // グリッド参照
  devCodeMap:  PropTypes.object,               // 架設設備コードマップ
  nearDevMap:  PropTypes.object,               // 最接近配電設備マップ 
  idoTypeMap:  PropTypes.object,               // 移動種別マップ
  isReadOnly:  PropTypes.bool.isRequired,      // 編集可否

  // イベント処理
  onItemEdit:   PropTypes.func.isRequired,    // 設備編集
  onItemSelect: PropTypes.func.isRequired,    // 設備選択
  onItemUpdate: PropTypes.func.isRequired,    // 設備更新
};
